import clsx from 'clsx';
import React, { useState } from 'react';
import Button from '../../components/Button';
import { useButtonSizeLarge } from '../../utils/buttonSizes';
import ReportIcon from '../../assets/icons/report-icon.svg?react';
import ChevronDown from '../../assets/icons/chevron-down-icon.svg?react';
import EditIcon from '../../assets/icons/edit-icon.svg?react';
import { toggleSwitch } from '../../reducers/switch';
import { useDispatch } from 'react-redux';
import ToggleIcon from '../../assets/icons/sidebar-toggle-icon.svg?react';
import { GetIncidentByIdResponse } from '@EHSNavigator/backend';

interface ReportIncidentProps {
  otherClasses?: string;
  draftIncidents: GetIncidentByIdResponse['incident'][];
}
const ReportIncident: React.FC<ReportIncidentProps> = ({
  otherClasses,
  draftIncidents,
}) => {
  const reportIncidentClasses = clsx(
    otherClasses,
    'w-full flex-1 pt-14 px-15 md:px-25 lg:pr-14 pb-10 lg:pb-20 lg:pl-8 flex flex-col lg:justify-between gap-12 md:gap-100 lg:gap-20',
  );
  const dispatch = useDispatch();
  const [toggle, setToggle] = useState(true);
  const sizes = useButtonSizeLarge();

  return (
    <div className={reportIncidentClasses} data-testid="report-incident">
      <div className="flex w-full flex-col gap-24">
        <div className="flex w-full max-w-818 flex-col gap-15">
          <h1 className="h3 hidden lg:block">Report an Incident</h1>
          <div className="flex items-center gap-3 lg:hidden">
            <button
              className="bg-translucent-background/80 border-neutral-tones-text/30 rounded-5 cursor-pointer border p-0.5"
              onClick={() => dispatch(toggleSwitch())}
            >
              <ToggleIcon />
            </button>
            <h3>Report an Incident</h3>
          </div>
          <p className="font-inter-variable text-neutral-tones-text/80 -tracking-button text-lg leading-5 font-medium lg:text-xl lg:leading-6">
            If you have encountered an incident, please report it here for quick
            and timely resolution of the issue.
          </p>
        </div>
        <div className="flex flex-col gap-25">
          <div className="flex gap-25">
            <Button size={sizes} href="/report-incident/report/">
              <ReportIcon className="[&>path]:fill-current" />
              Report Incident
            </Button>
            <Button
              disable
              href="/report-incident/drafts/"
              size={sizes}
              variant="secondary"
              otherClasses="relative"
            >
              <EditIcon className="h-6 w-6 [&>path]:fill-current" />
              Drafts
              {draftIncidents?.length > 0 && (
                <span className="text-neutral-tones-text/50 bg-brand-posh-pink font-inter-variable absolute -top-2 -right-2 flex h-5 w-5 items-center justify-center rounded-full px-2 py-1 text-xs font-normal text-white">
                  {draftIncidents?.length}
                </span>
              )}
            </Button>
          </div>
          <p className="font-inter-variable text-neutral-tones-text -tracking-button max-w-766 text-lg leading-5 font-medium italic lg:text-xl lg:leading-6">
            Verify that the incident/hazard meets the criteria below. Avoid
            submitting vague or unverified reports.
          </p>
        </div>
      </div>
      <div className="border-neutral-tones-text/35 rounded-10 w-full border bg-black/5 px-15 py-15">
        <button
          onClick={() => setToggle(!toggle)}
          className="font-inter-variable text-brand-byzantine -tracking-button flex w-full cursor-pointer items-center justify-between text-lg leading-6 font-bold"
        >
          Reporting Criteria & Guidelines
          <ChevronDown
            className={clsx(
              '[&>path]:fill-neutral-tones-text global-transition h-8 w-8',
              toggle ? 'rotate-180' : '',
            )}
          />
        </button>
        <div
          className={clsx(
            'global-transition',
            toggle
              ? 'mt-15 max-h-1000 overflow-auto'
              : 'mt-0 max-h-0 overflow-hidden',
          )}
        >
          <hr className="border-neutral-tones-text/10 border-[1px]" />
          <div className="mt-15 grid grid-cols-1 gap-25 lg:grid-cols-2">
            <div className="w-full">
              <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-6 font-bold md:text-lg">
                What is Classified as an Incident/Hazard?
              </h6>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                An incident/hazard is any event or condition that poses a risk
                to health, safety, or compliance, such as:
              </p>
              <ul className="my-5 list-[number] pl-5">
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Near Miss:</strong> A close call that could have
                  caused harm (e.g., a falling object barely missing a worker).
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Unsafe Act:</strong> Violations of safety protocols
                  (e.g., bypassing machine guards).
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Injury/Illness:</strong> Physical harm requiring
                  medical attention.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  <strong>Environmental Hazard:</strong> Spills, leaks, or
                  improper waste disposal.
                </li>
              </ul>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                Examples: Slippery floors, malfunctioning equipment, exposed
                wiring, or unsafe chemical handling.
              </p>
            </div>
            <div className="w-full">
              <h6 className="font-inter-variable text-neutral-tones-text -tracking-button mb-2.5 text-base leading-6 font-bold md:text-lg">
                What Should Be Reported?
              </h6>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                Report any situation that:
              </p>
              <ul className="my-5 list-[number] pl-5">
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  Poses immediate danger to personnel, equipment, or the
                  environment.
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  Violates safety standards (e.g., OSHA regulations).
                </li>
                <li className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                  Could lead to future harm if unaddressed (e.g., recurring
                  equipment issues).
                </li>
              </ul>
              <p className="font-inter-variable -tracking-form-input text-neutral-tones-text text-sm leading-5 font-normal md:text-base">
                Always report: Injuries, near misses, unsafe conditions, or
                procedural gaps.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportIncident;
